/**
 * "Continuá donde quedaste" de Escuchar: qué capítulo retomar, resuelto contra
 * el catálogo. Fuente única del hero del Home (`useHomeResume`) y del "Seguí
 * escuchando" de la Audioteca — antes cada pantalla tenía su propio criterio, y
 * la Audioteca directamente no tenía ninguno: sólo mostraba el capítulo abierto
 * en la pestaña, que muere al cerrar la ventana.
 *
 * Criterio: la entrada de progreso MÁS RECIENTE (el backend ya las devuelve
 * ordenadas por `updated_at DESC`) con posición > 0, saltando las que no
 * resuelven contra el catálogo (libro retirado, capítulo renombrado).
 *
 * La posición > 0 ES la invariante de "quedó a medias": terminar un capítulo lo
 * deja en 0 (`PATCH /listen/progress`), así que uno ya escuchado sólo vuelve a
 * aparecer acá si lo empezaste de nuevo — que es justo cuando querés retomarlo.
 */

import { bookKeypoints } from './bookLib.js';
import type {
  CatalogBook,
  CatalogKeypoint,
  Cefr,
  ChapterProgress,
  Edition,
  ProgressEntry,
} from './types.js';

export interface ListenResumeTarget {
  book: CatalogBook;
  chapter: CatalogKeypoint;
  edition: Edition;
  /** La fila de progreso que ganó (posición, nivel y duración real del mp3). */
  entry: ProgressEntry;
}

export function pickListenResume(
  progress: readonly ProgressEntry[],
  books: readonly CatalogBook[],
): ListenResumeTarget | null {
  for (const entry of progress) {
    if (entry.lastPositionSec <= 0) continue;
    const book = books.find((b) => b.slug === entry.book);
    if (!book) continue;
    const chapter = bookKeypoints(book, entry.edition).find((k) => k.slug === entry.keypoint);
    if (!chapter) continue;
    return { book, chapter, edition: entry.edition, entry };
  }
  return null;
}

/**
 * Segundo en el que abrir un capítulo, según el progreso que devolvió el
 * server. Sólo retoma si el progreso es de la MISMA rendition (el timeline
 * cambia con el nivel) y quedó a medias; el `> 1` evita pelear con el usuario
 * por un arranque de un segundo.
 *
 * NO mira `listenedAt`: terminar un capítulo deja la posición en 0, así que un
 * `lastPositionSec` > 1 ya significa "quedó a medias". Filtrar por
 * `listenedAt == null` hacía que reabrir un capítulo ya escuchado arrancara de
 * cero y —peor— que el primer PATCH del player pisara con 0 la posición real
 * que había guardada: perdías el momento justo al ir a retomarlo.
 */
export function resumePositionFor(progress: ChapterProgress | null, cefr: Cefr): number {
  if (!progress || progress.lastCefr !== cefr) return 0;
  return progress.lastPositionSec > 1 ? progress.lastPositionSec : 0;
}
